import { useState, useEffect, useReducer, useRef, useContext } from "react";
import {
  Button,
  Card,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import DataGrid from "../../components/dataGrid/DataGrid";
import { Link, useLocation, useNavigate } from "react-router-dom";
import QueryString from "qs";
import moment from "moment";
import { Check, Edit, Download, Upload } from "react-feather";
import {
  PickListData,
  exportExcelPickList,
  getPickLists,
  bulkImportPickList,
} from "../../services/pickList.service";
import toast from "react-hot-toast";
import FormInput from "../../components/FormInput";
import Dot from "../../components/Dot";
import FormSelect from "../../components/FormSelect";
import { getParties } from "../../services/parties.service";
import { CompanyContext } from "../../contexts/CompanyContext";
import { formatIndianAmount } from "../../utils/numberFormat";

type Status = "HOLD" | "PENDING" | "STARTED" | "STOPPED";

function mapStatus(status: Status): string {
  /* eslint-disable */
  const statusMapping: { [key in Status]: string } = {
    HOLD: "Hold",
    PENDING: "Pending",
    STARTED: "In Process",
    STOPPED: "Complete",
  };

  return statusMapping[status] || "Unknown";
  /* eslint-disable */
}

const initialState = {
  data: [],
  page: 1,
  limit: 100,
  totalPages: 1,
  totalResults: 0,
  startIndex: 1,
  endIndex: 100,
};

function reducer(state: any, action: any) {
  switch (action.type) {
    case "SET_DATA":
      return action.payload;
    default:
      return state;
  }
}

function PickLists() {
  const [filterData, setFilterData] = useState({
    party: "",
    orderNumber: "",
    workingStatus: "",
    date: "",
  });
  const [party, setParty] = useState("");
  const [workingStatus, setWorkingStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [data, dispatch] = useReducer(reducer, initialState);
  const location = useLocation();
  const navigate = useNavigate();
  const query = QueryString.parse(location.search, { ignoreQueryPrefix: true });
  const { selectedCompany }: any = useContext(CompanyContext);

  // Import modal state
  const [importModal, setImportModal] = useState(false);
  const [importSubmitting, setImportSubmitting] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<{
    successCount: number;
    failedCount: number;
    errors: string[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const toggleImportModal = () => {
    setImportModal((v) => !v);
    setImportFile(null);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleImportSubmit = () => {
    if (!importFile) return toast.error("Please select an Excel file.");

    setImportSubmitting(true);
    bulkImportPickList(importFile)
      .then((response) => {
        const { successCount, failedCount, errors } = response.data;
        setImportResult({ successCount, failedCount, errors: errors || [] });
        toast.success(`Import complete: ${successCount} inserted, ${failedCount} failed.`, {
          duration: 5000,
        });
        // Refresh the list
        const { page, party: qParty, orderNumber, workingStatus: qWS, date } = query;
        setLoading(true);
        getPickLists({ page, party: qParty, orderNumber, workingStatus: qWS, date }).then(
          (r: any) => {
            setData(r.data);
            setLoading(false);
          },
        );
      })
      .catch((error) => {
        toast.error(error?.response?.data?.message || "Import failed.");
        setLoading(false);
      })
      .finally(() => setImportSubmitting(false));
  };

  const columns = [
    {
      selector: (row: any) => row.serialNumber,
      name: "S.No.",
      sortable: false,
      //   wrap: true,
      maxWidth: "70px",
      minWidth: "70px",
    },

    {
      selector: (row: any) => (
        <div className={`${row.U ? "text-danger" : ""}`}>
          <div>{row.party?.name || ""}</div>
          <div>({row.party?.code || ""})</div>
        </div>
      ),
      name: "Party Name",
      sortable: false,
      wrap: true,
    },

    {
      selector: (row: PickListData) => (
        <div className={`${row.U ? "text-danger" : ""}`}>{row.orderNumber || ""}</div>
      ),
      name: "Order No.",
      sortable: false,
      wrap: true,
    },
    {
      selector: (row: any) => moment(row.date).format("DD/MM/YYYY"),
      name: "Order Date",
      sortable: false,
      wrap: true,
    },

    {
      selector: (row: PickListData) => formatIndianAmount(row.orderAmount),
      name: "Order Amount",
      sortable: false,
      wrap: true,
    },
    {
      selector: (row: PickListData) => row.lineItem,
      name: "Item",
      sortable: false,
      wrap: true,
    },
    {
      selector: (row: PickListData) => mapStatus(row.status),
      name: "Working Status",
      sortable: false,
      wrap: true,
    },
    {
      selector: (row: PickListData) => {
        return <>{row.VOR ? <Check strokeWidth={3} className='text-success' /> : ""}</>;
      },
      name: "VOR",
      sortable: false,
      wrap: true,
      maxWidth: "65px",
      minWidth: "65px",
    },
    {
      selector: (row: PickListData) => <Dot color={row.U ? "green" : "red"} />,
      name: "Urgent",
      sortable: false,

      maxWidth: "90px",
      minWidth: "90px",
      center: true,
    },

    {
      selector: (row: any) => (
        <>
          {row.s3Key && row.pdfDownloadUrl && (
            <button
              className='p-0 pb-1 me-1 btn btn-link'
              onClick={() => window.open(row.pdfDownloadUrl, "_blank")}
              title='Download PDF'
            >
              <Download size={15} />
            </button>
          )}
          <Link
            className='me-1'
            to={`edit/${row.id}?${QueryString.stringify({
              redirect: location.pathname + location.search,
            })}`}
          >
            <Edit size={15} />
          </Link>
        </>
      ),
      name: "Actions",
      sortable: false,
      right: true,
      maxWidth: "130px",
      minWidth: "130px",
    },
  ];

  const setData = (data: any) => {
    dispatch({ type: "SET_DATA", payload: data });
  };
  useEffect(() => {
    const { page, party, orderNumber, workingStatus, date } = query;
    setLoading(true);
    getPickLists({ page, party, orderNumber, workingStatus, date })
      .then((response: any) => {
        setData(response.data);
      })
      .catch((error) => {})
      .finally(() => {
        setLoading(false);
      });
  }, [location.search]);

  const handleFilter = () => {
    const queryParams: any = { ...query, page: 1, ...filterData };

    Object.keys(queryParams).forEach((key) => {
      if (!queryParams[key]) {
        delete queryParams[key];
      }
    });

    const queryString = QueryString.stringify(queryParams);
    navigate(`${location.pathname}${Object.keys(queryParams).length > 0 ? "?" + queryString : ""}`);
  };
  const handleClearFilter = () => {
    setWorkingStatus("");
    setFilterData({ party: "", orderNumber: "", workingStatus: "", date: "" });
    const queryParams = { page: 1 };
    const queryString = QueryString.stringify(queryParams);
    navigate(`${location.pathname}?${queryString}`);
  };
  return (
    <div className='d-flex flex-column h-100 px-sm-1 w-100 pb-sm-1'>
      <Row className='align-items-center mb-2 page-title-row'>
        <Col sm>
          <h1 className='my-1 fw-bold h4'>Pick List</h1>
        </Col>
        <Col sm='auto'>
          <div className='justify-content-between d-flex gap-2'>
            <Button size='sm' color='secondary' onClick={toggleImportModal} className='my-1'>
              <Upload size={14} className='me-1' />
              Import
            </Button>

            <Link
              to={`/pick-lists/add?${QueryString.stringify({
                redirect: location.pathname + location.search,
              })}`}
              className='btn btn-sm btn-primary my-1 text-white'
            >
              Add New
            </Link>
          </div>
        </Col>
      </Row>

      <div className='flex-grow-1 overflow-hidden' style={{ height: "calc(100vh - 230px)" }}>
        <Card className='h-100 p-3 w-100 '>
          <div className='d-flex flex-column h-100 rounded-0 list-data-grid-wrapper'>
            <DataGrid
              columns={columns}
              data={data}
              loading={loading}
              isFilterApplied={Object.keys(query).some((key) => key !== "page")}
              noDataTitle='No pick lists found'
              noDataSubtitle='There are no pick lists available right now.'
              noDataFilteredTitle='No pick lists match your filters'
              noDataFilteredSubtitle='Try changing or clearing the filter criteria.'
              filterByDate={true}
              FilterSubHeader={true}
              filterData={filterData}
              setFilterData={setFilterData}
              subHeaderComponent={
                <Row className='w-100 align-items-start g-3 py-1 px-1'>
                  <Col md={2}>
                    <FormSelect
                      id='party'
                      name='party'
                      label='Party'
                      isAsync
                      isClearable
                      defaultFilter={{ status: true }}
                      getOptions={getParties}
                      onChange={(name: any, value: any) => {
                        setParty(value);
                        setFilterData({
                          ...filterData,
                          [name]: value?.id,
                        });
                      }}
                      getOptionLabel={(row: any) => `${row.name} (${row.code})`}
                      formatOptionLabel={(row: any, { context }: any) =>
                        context === "menu" ? (
                          <>
                            <small>{row.name}</small>
                            <div>({row.code})</div>
                          </>
                        ) : (
                          <span>
                            {row.name} ({row.code})
                          </span>
                        )
                      }
                      value={party}
                    />
                  </Col>
                  <Col md={2}>
                    <FormInput
                      name='orderNumber'
                      label='Order No.'
                      placeholder='Order No.'
                      onChange={(e) =>
                        setFilterData({
                          ...filterData,
                          [e.target.name]: e.target.value,
                        })
                      }
                      value={filterData.orderNumber}
                    />
                  </Col>
                  <Col md={2}>
                    <FormSelect
                      name='workingStatus'
                      label='Working Status'
                      options={[
                        { id: "Pending", name: "Pending" },
                        { id: "Hold", name: "Hold" },
                        { id: "In Process", name: "In Process" },
                        { id: "Complete", name: "Complete" },
                      ]}
                      isClearable
                      value={workingStatus}
                      onChange={(name: any, value: any) => {
                        setWorkingStatus(value);
                        setFilterData({
                          ...filterData,
                          [name]: value?.id,
                        });
                      }}
                    />
                  </Col>
                  <Col md={2}>
                    <FormInput
                      name='date'
                      label='Date'
                      type='date'
                      onChange={(e) =>
                        setFilterData({
                          ...filterData,
                          [e.target.name]: e.target.value,
                        })
                      }
                      value={filterData.date}
                    />
                  </Col>

                  <Col md='auto'>
                    <div className='filter-action-buttons'>
                      <Button color='primary' size='sm' onClick={() => handleFilter()}>
                        Filter
                      </Button>
                      <Button color='danger' size='sm' onClick={() => handleClearFilter()}>
                        Clear
                      </Button>
                    </div>
                  </Col>
                  <Col className='ms-auto' md='auto'>
                    <div className='filter-export-button'>
                      <Button
                        color='primary'
                        size='sm'
                        onClick={() => {
                          const { party, orderNumber, workingStatus, date } = query;

                          exportExcelPickList({ party, orderNumber, workingStatus, date })
                            .then((response) => {
                              const blobURL = URL.createObjectURL(response.data);
                              const anchor = document.createElement("a");
                              anchor.href = blobURL;
                              anchor.download = "Pick List.xlsx";
                              anchor.click();
                              URL.revokeObjectURL(blobURL);
                            })
                            .catch(() => {
                              toast.error("Record not found to export.");
                            });
                        }}
                      >
                        Export
                      </Button>
                    </div>
                  </Col>
                </Row>
              }
            />
          </div>
        </Card>
      </div>

      {/* Import Modal */}
      <Modal isOpen={importModal} toggle={toggleImportModal}>
        <ModalHeader toggle={toggleImportModal}>Bulk Import Pick Lists</ModalHeader>
        <ModalBody>
          <p className='text-muted' style={{ fontSize: "0.85rem" }}>
            Upload an Excel file with columns:{" "}
            <strong>Party Code, PickList No, Date, Invoice Amount, Line Item, VOR, U</strong>.
          </p>
          <label className='form-label'>Excel File (.xls / .xlsx)</label>
          <input
            ref={fileInputRef}
            type='file'
            accept='.xls,.xlsx'
            style={{ display: "none" }}
            onChange={(e) => setImportFile(e.target.files?.[0] || null)}
          />
          <div className='d-flex align-items-stretch border rounded overflow-hidden bg-white'>
            <button
              type='button'
              className='btn btn-light border-0 border-end rounded-0 px-3'
              onClick={() => fileInputRef.current?.click()}
            >
              Choose file
            </button>
            <div
              className='d-flex align-items-center px-3 text-muted flex-grow-1'
              style={{ minHeight: "38px", fontSize: "0.875rem" }}
            >
              {importFile?.name || "No file selected"}
            </div>
          </div>

          {importResult && (
            <div className='mt-3'>
              <div className='d-flex gap-3 mb-2'>
                <div className='px-3 py-2 rounded bg-light border'>
                  <div className='small text-muted'>Inserted</div>
                  <div className='fw-bold text-success'>{importResult.successCount}</div>
                </div>
                <div className='px-3 py-2 rounded bg-light border'>
                  <div className='small text-muted'>Failed</div>
                  <div className='fw-bold text-danger'>{importResult.failedCount}</div>
                </div>
              </div>

              {importResult.errors.length > 0 && (
                <div>
                  <div className='fw-semibold mb-2'>Failed rows</div>
                  <div
                    className='border rounded p-2 bg-light'
                    style={{ maxHeight: "220px", overflowY: "auto", fontSize: "0.85rem" }}
                  >
                    {importResult.errors.map((error, index) => (
                      <div key={`${index}-${error}`} className='text-danger mb-1'>
                        {error}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color='secondary'
            size='sm'
            onClick={toggleImportModal}
            disabled={importSubmitting}
          >
            Cancel
          </Button>
          <Button
            color='primary'
            size='sm'
            onClick={handleImportSubmit}
            disabled={importSubmitting}
          >
            {importSubmitting ? <Spinner size='sm' className='me-1' /> : null}
            Import
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default PickLists;
